/* What a tax return needs to know, gathered from what the book already holds.
 *
 * This reports figures. It does not tell anyone what they owe, and it is not
 * advice — §1's rule about advice applies here more than anywhere, because a
 * confident wrong number in this particular place costs money. Every line says
 * which account and which currency it came from, so each one can be checked
 * against the broker's own statement.
 *
 * Two decisions worth knowing about:
 *
 * Amounts are kept in the currency they happened in and are never silently
 * converted. A Polish return wants each amount at the National Bank's rate for
 * the business day *before* the transaction, and converting a year of trades at
 * today's rate would produce a number that looks official and is wrong. The
 * conversion is offered separately, using the dated rates, and says so.
 *
 * A tax-sheltered account is reported and excluded from the taxable totals, not
 * hidden. Seeing that an account contributed nothing is part of checking that
 * the return is right. */

import type { AccountRecord, Dividend, Trade } from '../types';

export interface TaxLine {
  account: string;
  accountName: string;
  currency: string;
  /** Realised on sales closed in the year, in `currency`. */
  realised: number;
  sales: number;
  dividendGross: number;
  dividendWithheld: number;
  dividendNet: number;
  dividends: number;
  /** Gains here are outside the tax base — reported so the omission is visible. */
  shielded: boolean;
}

export interface TaxReport {
  year: number;
  lines: TaxLine[];
  /** Totals by currency across everything not sheltered. */
  taxable: Array<{ currency: string; realised: number; dividendGross: number; dividendWithheld: number }>;
  /** Sales with a gain recorded against them, and sales without. */
  salesWithGain: number;
  salesWithoutGain: number;
}

const yearOf = (iso: string) => Number(iso.slice(0, 4));

export function buildTaxReport(input: {
  year: number;
  trades: Trade[];
  dividends: Dividend[];
  accounts: AccountRecord[];
}): TaxReport {
  const { year, trades, dividends, accounts } = input;
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const lines = new Map<string, TaxLine>();

  const lineFor = (accountId: string, currency: string): TaxLine => {
    const key = `${accountId}|${currency}`;
    const found = lines.get(key);
    if (found) return found;
    const account = byId.get(accountId);
    const made: TaxLine = {
      account: accountId,
      accountName: account?.name ?? accountId,
      currency,
      realised: 0,
      sales: 0,
      dividendGross: 0,
      dividendWithheld: 0,
      dividendNet: 0,
      dividends: 0,
      shielded: account?.taxShielded === true,
    };
    lines.set(key, made);
    return made;
  };

  let salesWithGain = 0;
  let salesWithoutGain = 0;

  for (const t of trades) {
    if (t.side !== 'sell' || yearOf(t.at) !== year) continue;
    const account = byId.get(t.account);
    // A trade carries no currency of its own; the account it happened in does.
    const line = lineFor(t.account, account?.currency ?? 'USD');
    line.sales += 1;
    if (typeof t.realizedGain === 'number') {
      line.realised += t.realizedGain;
      salesWithGain += 1;
    } else {
      /* A sale with no gain recorded is left out of the total rather than
         counted as zero — a zero would quietly understate the year. */
      salesWithoutGain += 1;
    }
  }

  for (const d of dividends) {
    if (yearOf(d.payDate) !== year) continue;
    const line = lineFor(d.account, d.currency);
    line.dividendGross += d.gross;
    line.dividendWithheld += d.taxWithheld;
    line.dividendNet += d.net;
    line.dividends += 1;
  }

  const taxableByCurrency = new Map<string, { currency: string; realised: number; dividendGross: number; dividendWithheld: number }>();
  for (const line of lines.values()) {
    if (line.shielded) continue;
    const t = taxableByCurrency.get(line.currency)
      ?? { currency: line.currency, realised: 0, dividendGross: 0, dividendWithheld: 0 };
    t.realised += line.realised;
    t.dividendGross += line.dividendGross;
    t.dividendWithheld += line.dividendWithheld;
    taxableByCurrency.set(line.currency, t);
  }

  return {
    year,
    lines: [...lines.values()].sort((a, b) =>
      a.accountName.localeCompare(b.accountName) || a.currency.localeCompare(b.currency)),
    taxable: [...taxableByCurrency.values()].sort((a, b) => a.currency.localeCompare(b.currency)),
    salesWithGain,
    salesWithoutGain,
  };
}

/** Every date whose rate a conversion would need: the business day before each
    taxable event, which is what a Polish return asks for. */
export function ratesNeeded(report: TaxReport, trades: Trade[], dividends: Dividend[]): string[] {
  const dates = new Set<string>();
  const shielded = new Set(report.lines.filter((l) => l.shielded).map((l) => l.account));
  const dayBefore = (iso: string) =>
    new Date(new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime() - 86_400_000).toISOString().slice(0, 10);

  for (const t of trades) {
    if (t.side !== 'sell' || yearOf(t.at) !== report.year || shielded.has(t.account)) continue;
    dates.add(dayBefore(t.at));
  }
  for (const d of dividends) {
    if (yearOf(d.payDate) !== report.year || shielded.has(d.account)) continue;
    dates.add(dayBefore(d.payDate));
  }
  return [...dates].sort();
}

/* ---------- converting, at the rate of the day before ---------- */

export interface Converted {
  realised: number;
  dividendGross: number;
  dividendWithheld: number;
  /** Dates whose rate never arrived. Anything they touched is left out, and the
      caller says so rather than presenting a short total as a complete one. */
  missing: string[];
  skipped: number;
}

/** Sums a year into one currency using a dated rate per event.
 *
 * `rate` is asked for the currency and the business day before the transaction,
 * which is what a Polish return specifies. Anything it cannot answer for is
 * skipped and counted, never converted at some other day's rate — that is the
 * whole reason this is a separate step and not the headline figure. */
export function convertYear(input: {
  year: number;
  trades: Trade[];
  dividends: Dividend[];
  accounts: AccountRecord[];
  rate: (currency: string, onDate: string) => number | null;
}): Converted {
  const { year, trades, dividends, accounts, rate } = input;
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const dayBefore = (iso: string) =>
    new Date(new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime() - 86_400_000).toISOString().slice(0, 10);

  const out: Converted = { realised: 0, dividendGross: 0, dividendWithheld: 0, missing: [], skipped: 0 };
  const missing = new Set<string>();

  for (const t of trades) {
    if (t.side !== 'sell' || yearOf(t.at) !== year) continue;
    const account = byId.get(t.account);
    if (account?.taxShielded) continue;
    if (typeof t.realizedGain !== 'number') { out.skipped += 1; continue; }
    const on = dayBefore(t.at);
    const r = rate(account?.currency ?? 'USD', on);
    if (r === null) { missing.add(on); out.skipped += 1; continue; }
    out.realised += t.realizedGain * r;
  }

  for (const d of dividends) {
    if (yearOf(d.payDate) !== year) continue;
    if (byId.get(d.account)?.taxShielded) continue;
    const on = dayBefore(d.payDate);
    const r = rate(d.currency, on);
    if (r === null) { missing.add(on); out.skipped += 1; continue; }
    out.dividendGross += d.gross * r;
    out.dividendWithheld += d.taxWithheld * r;
  }

  out.missing = [...missing].sort();
  return out;
}
